import { useState, type FormEvent } from 'react';

import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

/**
 * The username of the last successful sign-in, so "remember me" also prefills who you are.
 *
 * Only the username, never the password — a password in localStorage is readable by any script
 * that ever runs on this origin. The session itself lives in an httpOnly cookie the page cannot
 * read at all.
 */
const REMEMBERED_USER = 'rfid_mw_remembered_user';

export default function Login() {
  const { refresh } = useAuth();
  const [username, setUsername] = useState(() => localStorage.getItem(REMEMBERED_USER) ?? '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(() => localStorage.getItem(REMEMBERED_USER) !== null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/auth/login', { username, password, remember });
      if (remember) localStorage.setItem(REMEMBERED_USER, username);
      else localStorage.removeItem(REMEMBERED_USER);
      await refresh();
    } catch (err) {
      // Whatever the server said — it is deliberately vague about which half was wrong.
      setError((err as Error).message);
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-8 shadow-sm"
      >
        <h1 className="text-lg font-semibold text-slate-900">LSK RFID &amp; BI</h1>
        <p className="mt-1 text-sm text-slate-500">Sign in to continue.</p>

        <label className="mt-6 block text-sm font-medium text-slate-700" htmlFor="username">
          Username
        </label>
        <input
          id="username"
          name="username"
          autoComplete="username"
          autoFocus
          required
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
        />

        <label className="mt-4 block text-sm font-medium text-slate-700" htmlFor="password">
          Password
        </label>
        <div className="relative mt-1">
          <input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded border border-slate-300 py-2 pl-3 pr-16 text-sm focus:border-brand-500 focus:outline-none"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            // `tabIndex={-1}` so tabbing runs username → password → sign in. A toggle that
            // intercepts the tab out of the password field makes the form feel broken.
            tabIndex={-1}
            aria-pressed={showPassword}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            className="absolute inset-y-0 right-0 px-3 text-xs font-medium text-slate-500 hover:text-brand-600"
          >
            {showPassword ? 'Hide' : 'Show'}
          </button>
        </div>

        <label className="mt-4 flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            className="rounded border-slate-300"
          />
          Keep me signed in for 30 days
        </label>

        {error && (
          <p role="alert" className="mt-4 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="mt-6 w-full rounded bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
